import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    NgZone,
    OnInit,
    ViewEncapsulation,
    inject,
    input,
    signal,
    viewChild,
} from '@angular/core';
import { filter, find, includes, isEmpty, last as _last, map } from 'lodash-es';
import { ParseService } from 'apps/account/src/account-shared/services/parse.service';
import { MessagingV1ApiService } from 'apps/account/src/account-shared/services/messaging-v1-api.service';
import { endpointUrl } from 'apps/account/src/account-shared/services/endpoint-url.util';
import { changesTransformer, transformType } from 'apps/account/src/account-shared/services/changes-transformer.util';
import { VersionSelectionService } from 'apps/account/src/account-shared/services/version-selection.service';
import { LivePreviewService } from 'apps/account/src/account-shared/services/live-preview.service';
import { NotificationsService } from 'apps/account/src/account-shared/services/notifications.service';
import { ConfirmationImageV1ApiService } from 'apps/account/src/account-shared/services/confirmation-image-v1-api.service';
import { DEFAULT_LANGUAGE } from 'apps/account/src/account-shared/services/language-helper.util';
import { ImageV1ApiService } from 'apps/account/src/account-shared/services/image-v1-api.service';
import { IdentityService } from 'account-hybrid/core/authentication';
import { SpacesService } from 'account-hybrid/common/components/spaces';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FtLanguageSelectorComponent } from '../contest-sheets/ft-language-selector.component';
import { CurationBackgroundImageDirective } from '../../stories/curation-card/background-image.directive';
import { FieldEditorModalConfig, FtFieldEditorModalComponent } from '../contest-sheets/field-editor-modal.component';
import { FtPendingChangesPanelComponent } from './ft-pending-changes-panel.component';
import { computeDiffs, revertDiffs, snapshotClone } from '../contest-sheets/native-diff.util';
import { FtSelectComponent } from './ft-select.component';
import { FtImageLibraryComponent } from './ft-image-library.component';
import { FtModalComponent } from '../../../common/components/modal/ft-modal.component';
import { FtModalChromeComponent } from '../../../common/components/modal/ft-modal-chrome.component';
import { FtDisabledDirective } from './ft-disabled.directive';
import { FtLaunchPreviewUnavailableDirective } from './ft-launch-preview-unavailable.directive';

/** Insert-Token dropdown items — v1 token shapes: `'first'` inserts `{first}`,
 * `{text,wrapper}` inserts `<wrapper>{text}</wrapper>` (FtHtmlField insertToken). */
type FieldTokens = Array<string | { text: string; wrapper: string }>;

/**
 * Native port of the AngularJS confirmation-touchpoint MESSAGING editor. Read-view rendered
 * verbatim (global LESS classes); edit machinery is native (field-editor modal + pending-changes
 * deferred-save panel + homepage overlays) over the native V1Api services. The `messaging` payload
 * + identifiers are passed in from the container (single Messaging.get, no duplicate /api).
 */
@Component({
    selector: 'ft-touchpoint-messaging-ng',
    imports: [CommonModule, TranslateModule, FtLanguageSelectorComponent, CurationBackgroundImageDirective, FtFieldEditorModalComponent, FtPendingChangesPanelComponent, FtSelectComponent, FtImageLibraryComponent, FtModalComponent, FtModalChromeComponent, FtDisabledDirective, FtLaunchPreviewUnavailableDirective],
    encapsulation: ViewEncapsulation.None,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    styleUrls: ['./ft-touchpoint-messaging.component.scss'],
    templateUrl: './ft-touchpoint-messaging.component.html',
})
export class FtTouchpointMessagingComponent implements OnInit, AfterViewInit {
    readonly messaging = input<any>();
    readonly layerName = input.required<string>();
    readonly touchpointType = input.required<string>();
    readonly touchpointGroupId = input.required<string>();
    readonly propertyUuid = input.required<string>();

    readonly panel = viewChild<FtPendingChangesPanelComponent>('panel');

    /** ftModal phase 2 / WS3: the native field-editor modal request (null = closed). */
    protected readonly fieldEditorConfig = signal<FieldEditorModalConfig | null>(null);
    /** Field-level open-time snapshot of the edited datasource, for the field modal's cancel-revert. */
    private fieldSnapshot: any = null;
    /** The object the open field editor mutates (model.languageItems). */
    private fieldDatasource: any = null;

    /** homepage: which native modal is open (null = none) — the v1 dialogType/imageLibrary ftModals. */
    protected readonly homepageModal = signal<'dialogTypeModal' | 'imageLibraryModal' | null>(null);
    /** homepage modal open-time ft-snapshot (model.messaging / model.languageItems) for X/Cancel revert. */
    private homepageModalSnapshot: any = null;
    private homepageModalRoot: any = null;

    protected readonly model = signal<any>({
        languages: [],
        selectedLanguage: {},
        languageItems: [],
        language: {},
    });
    private version: any = {};

    /**
     * nav-pane screen state — the v1 ftNavPaneItem `default` seed. followup uses
     * 'followup'(default)/'share'; reminder-emails uses 'non-view'(default)/'after-view';
     * reengagement-emails uses 'no-quote'(default)/'no-photo'. Seeded per touchpointType in ngOnInit.
     */
    protected readonly screenName = signal<
        | 'followup' | 'share' | 'non-view' | 'after-view' | 'no-quote' | 'no-photo' | 'signup' | 'signup-guest' | 'mobile' | 'thankyou' | 'unlock'
        // WS4 inc 6 grind: quote "Post-stay Process" nav-pane screens (edit-quote-messages-v2.html).
        | 'intro' | 'poststayrating' | 'quoteFeedback' | 'contest' | 'thanks' | 'feedbackLowRating' | 'thanksLowRating' | 'introFromEmail' | 'introFromEmailLowRating'>('followup');

    private dataByLanguage: any = {};

    private readonly zone = inject(NgZone);
    private readonly sanitizer = inject(DomSanitizer);

    // WS5 #10 inc 2w: the LAST $injector grabs drained — every v1 service here is native.
    private readonly messagingApi: any = inject(MessagingV1ApiService);
    private readonly endpointUrl: any = endpointUrl;
    private readonly changesTransformer: any = changesTransformer;
    private readonly transformType: any = transformType;
    private readonly versionSelection: any = inject(VersionSelectionService);
    private readonly livePreview: any = inject(LivePreviewService);
    private readonly identity: any = inject(IdentityService);
    private readonly spaces: any = inject(SpacesService);
    private readonly parse: any = inject(ParseService);
    private readonly notifications: any = inject(NotificationsService);
    private readonly confirmationImage: any = inject(ConfirmationImageV1ApiService);
    private readonly defaultLanguage: string = DEFAULT_LANGUAGE;
    private readonly rtlLanguages: any = ['ar', 'he']; // v1 .constant('rtlLanguages')
    private readonly image: any = inject(ImageV1ApiService);
    private readonly translateSvc = inject(TranslateService);
    private readonly translate: (key: string, params?: any) => string = (key: string, params?: any) => this.translateSvc.instant(key, params);

    /** v1 ftDirection: rtl/ltr per the selected language (isRtl flag or the rtlLanguages list). */
    protected textDirection(): string {
        const lang = this.model() && this.model().selectedLanguage;
        if (!lang) return 'ltr';
        const isRtl = lang.isRtl !== undefined && lang.isRtl !== null ? this.parse.bool(lang.isRtl) : includes(this.rtlLanguages, lang.code);
        return isRtl ? 'rtl' : 'ltr';
    }

    protected isFliptoStaff(): boolean {
        return this.identity.isFliptoStaff();
    }

    ngOnInit(): void {
        // Seed the nav-pane default screen per touchpointType (the v1 ftNavPaneItem `default`):
        // reminder-emails → 'non-view'; reengagement-emails → 'no-quote'; followup keeps 'followup'.
        if (this.touchpointType() === 'reminder-emails') {
            this.screenName.set('non-view');
        } else if (this.touchpointType() === 'reengagement-emails') {
            this.screenName.set('no-quote');
        } else if (this.touchpointType() === 'landing') {
            this.screenName.set('signup');
        } else if (this.touchpointType() === 'mobile') {
            this.screenName.set('mobile');
        } else if (this.touchpointType() === 'homepage') {
            this.screenName.set('unlock');
            this.activateHomepage();
            return;
        } else if (this.touchpointType() === 'quote') {
            // quote "Post-stay Process" (edit-quote-messages-v2.html) — the v1 ftNavPaneItem `default` is intro.
            this.screenName.set('intro');
        }
        this.activate();
    }

    ngAfterViewInit(): void {
        // ftModal phase 2 / WS3 COMPLETE for this component: EVERY messaging branch is now NATIVE — the
        // template's `ft-pending-changes-panel-ng` ([live]=panelLiveRoot: model.messaging for homepage,
        // model.languageItems for the rest) + the native `ft-field-editor-modal` field editors + the
        // native homepage `ft-modal-overlay` QuoteType/ProfileImage modals. There is NO bridged
        // `fieldsEditor.compile` left. (homepage was the last: its v1 `<ft-pending-changes-panel
        // ft-snapshot=model.messaging>` + the dialogType `<ft-select>` + image-library `<ft-image-library>`
        // compiles are replaced by the native panel/modals above. reminder/reengagement v1
        // setup-snapshot-proxy set an inert model.hasPendingChanges flag — gate-blind, dropped.)
    }

    // --- read-view helpers ---

    protected get selectedData(): any {
        const code = this.model()?.selectedLanguage?.code;
        return (code && this.model()?.language?.[code]) || {};
    }

    protected trust(value: any): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(value || '');
    }

    /**
     * Reproduces the v1 `ft-placeholder=""` directive on a `ng-bind-html` field: when the bound
     * HTML is empty, the directive appends `<span class="placeholder">Click to edit</span>` to the
     * element. Emit that span (as the element's innerHTML) for the empty case so empty fields render
     * the "Click to edit" placeholder byte-identically to v1 (the landing/mobile message fields carry
     * ft-placeholder; on an unconfigured touchpoint like mobile they're empty → the placeholder shows).
     */
    protected placeholderHtml(value: any): SafeHtml {
        if (value) return this.sanitizer.bypassSecurityTrustHtml(value);
        return this.sanitizer.bypassSecurityTrustHtml(`<span class="placeholder">${this.translate('flipto.account.ClickToEdit')}</span>`);
    }

    protected getCurrentLanguage(): any {
        if (this.model() && this.model().selectedLanguage) {
            return find(this.model().languageItems, { code: this.model().selectedLanguage.code });
        }
        return undefined;
    }

    // --- quote "Post-stay Process" helpers + edit handlers (edit-quote-messages-v2.html) ---

    /** v1 `value | parse: 'bool'` in a template expression. */
    protected parseBool(value: any): boolean {
        return this.parse.bool(value);
    }

    /** ft-placeholder with a CUSTOM placeholder text (quote's quotePlaceSelectionLabel). */
    protected placeholderHtmlText(value: any, text: string): SafeHtml {
        if (value) return this.sanitizer.bypassSecurityTrustHtml(value);
        return this.sanitizer.bypassSecurityTrustHtml(`<span class="placeholder">${text}</span>`);
    }

    /** v1 openPoststayPreview() — livePreview.poststay (version + selected language). */
    protected openPoststayPreview(): void {
        this.livePreview.poststay({
            propertyUUID: this.spaces.getPropertyUuid(),
            version: this.version.currentVersion,
            languageCode: this.model().selectedLanguage.code,
        });
    }

    private openQuoteEditor(_key: string, field: string, title: string, editorType: 'html' | 'text' = 'html', tokens?: FieldTokens): void {
        // quote "Post-stay Process" — native via openField. On quebec there is NO upgrade version
        // (`<ft-version-panel>` empty), and these editors only mutate the MAIN version (model.languageItems);
        // the v1 dual-version snapshot (model.messaging.languages.item, ...upgrade.languages.item) is reduced
        // to the single main root. ⚠ DEFERRED (gate-blind on quebec — no upgrade): tracking the upgrade
        // version (only changed by the version-migration cross-flow, not by these field editors).
        this.openField({ editorType, fields: [field], tokens, title });
    }

    protected editIntroCaption(): void {
        this.openQuoteEditor('introCaption', 'introCaption', this.translate('flipto.account.IntroMessage'), 'html', ['first','last']);
    }
    protected editIntroContestCaption(): void {
        this.openQuoteEditor('introContestCaption', 'introContestCaption', this.translate('flipto.account.ContestDetails'), 'html', ['photo-contest-name']);
    }
    protected editIntroRewardsCaption(): void {
        this.openQuoteEditor('introRewardsCaption', 'introRewardsCaption', this.translate('flipto.account.RewardsDetails'));
    }
    protected editContestCaption(): void {
        this.openQuoteEditor('contestCaption', 'contestCaption', this.translate('flipto.account.JoinContestMessage'), 'html', ['first', 'last']);
    }
    protected editQuotePositiveNotes(): void {
        this.openQuoteEditor('quotePositiveNotesLabel', 'quotePositiveNotesLabel', this.translate('flipto.account.QuoteMessage'));
    }
    protected editQuotePlaceSelection(): void {
        this.openQuoteEditor('quotePlaceSelectionLabel', 'quotePlaceSelectionLabel', 'Place message');
    }
    protected editQuotePositiveOptional(): void {
        this.openQuoteEditor('quotePositiveOptionalLabel', 'quotePositiveOptionalLabel', this.translate('flipto.account.FeedbackMessage'));
    }
    protected editShareCaption(): void {
        this.openQuoteEditor('shareCaption', 'shareCaption', this.translate('flipto.account.ShareMessage'), 'html', ['first','last']);
    }
    protected editShareMessage(): void {
        this.openQuoteEditor('shareMessage', 'shareMessage', this.translate('flipto.account.platform.postStay.touchpoints.ShareMessage'), 'text', ['review']);
    }
    protected editThanksCaptionPositive(): void {
        this.openQuoteEditor('thanksCaptionPositive', 'thanksCaptionPositive', this.translate('flipto.account.ThanksMessage'), 'html', ['first', 'last']);
    }
    protected editThanksExternalLink(): void {
        this.openQuoteEditor('thanksExternalLinkLabel', 'thanksExternalLinkLabel', this.translate('flipto.account.ExternalReviewMessage'));
    }
    protected editQuoteNegativeNotes(): void {
        this.openQuoteEditor('quoteNegativeNotesLabel', 'quoteNegativeNotesLabel', this.translate('flipto.account.FeedbackMessage'), 'html', ['first','last']);
    }
    protected editThanksCaptionNegative(): void {
        this.openQuoteEditor('thanksCaptionNegative', 'thanksCaptionNegative', this.translate('flipto.account.ThanksMessage'), 'html', ['first', 'last']);
    }
    protected editIntroCaptionPositiveRating(): void {
        this.openQuoteEditor('introCaptionPositiveRating', 'introCaptionPositiveRating', this.translate('flipto.account.IntroMessage'), 'html', ['first', 'last']);
    }
    protected editIntroCaptionNegativeRating(): void {
        this.openQuoteEditor('introCaptionNegativeRating', 'introCaptionNegativeRating', this.translate('flipto.account.IntroMessage'), 'html', ['first', 'last']);
    }

    // --- model setup (ported from MessagingController.activate) ---

    private activate(): void {
        const messaging = this.messaging();
        this.prepare(messaging);

        const languages = messaging.languages.item;
        const upgradeLanguages = messaging.upgrade && messaging.upgrade.languages.item;

        this.model.set({
            propertyId: this.spaces.getPropertyUuid(),
            languages: [],
            selectedLanguage: {},
            messaging,
            isActive: messaging.isActive === '1' || messaging.isEmailActive === '1',
            isPhotoAdvocacyEnabled: messaging.isPhotoAdvocacyEnabled === '1',
            isNewQuoteProcess: messaging.isNewQuoteProcess,
            languageItems: languages,
            isCanSendEmail: this.parse.bool(messaging.isCanSendEmail),
            language: {},
        });
        const model = this.model();

        this.setupVersionManager(messaging);

        this.dataByLanguage = {};
        const upgradeDataByLanguage: any = {};
        languages &&
            languages.forEach((language: any) => {
                language.data = language.data || {};
                this.dataByLanguage[language.code] = language.data;
            });
        upgradeLanguages &&
            upgradeLanguages.forEach((language: any) => {
                language.data = language.data || {};
                upgradeDataByLanguage[language.code] = language.data;
            });

        model.language = this.version.currentIsNewVersion() ? upgradeDataByLanguage : this.dataByLanguage;
        model.languageItems = this.version.currentIsNewVersion() ? upgradeLanguages : languages;

        if (model.languages.length === 0 && languages) {
            languages.forEach((language: any) => {
                model.languages.push({ code: language.code, name: language.name });
            });
        }

        // The v1 ftLanguageSelector auto-selects defaultLanguage when selectedLanguage is
        // empty; the native FtLanguageSelectorComponent doesn't, so seed it here (else the
        // read-view's selectedData[...] reads off an undefined code).
        model.selectedLanguage = find(model.languages, { code: this.defaultLanguage }) || model.languages[0] || {};

        // v1 MessagingController.activate: create the confirmation banner image when missing
        // (only for confirmation-email-image; skipped on quebec where images already exist).
        if (this.touchpointType() === 'confirmation-email-image' && !this.parse.bool(messaging.hasConfirmationEmailImages)) {
            this.confirmationImage.refresh(this.touchpointGroupId(), 'en', this.spaces.getPropertyUuid());
        }
    }

    // --- HOMEPAGE "Homepage Component" model setup (the SPECIAL HomepageMessagingController) ---

    private activateHomepage(): void {
        const messaging = this.messaging();
        messaging.isReplacePictures = messaging.isReplacePictures === '1';
        messaging.isUseFixedQuote = messaging.isUseFixedQuote === '1';
        this.model.set({
            propertyId: this.spaces.getPropertyUuid(),
            languages: [],
            selectedLanguage: {},
            messaging,
            isActive: messaging.isActive === '1',
            language: {},
            languageItems: messaging.languages.item,
        });
        const model = this.model();
        const languages = messaging.languages.item;
        languages &&
            languages.forEach((language: any) => {
                this.setupLanguagesData(language);
                model.language[language.code] = language.data;
            });
        if (model.languages.length === 0 && languages) {
            languages.forEach((language: any) => model.languages.push({ code: language.code, name: language.name }));
        }
        // the native ftLanguageSelector doesn't auto-select defaultLanguage → seed it.
        model.selectedLanguage = find(model.languages, { code: this.defaultLanguage }) || model.languages[0] || {};

        const prefix = 'flipto.account.platform.homepage.touchpoints.';
        model.dialogTypes = [
            { id: 0, name: this.translate(prefix + 'GuestQuote') },
            { id: 1, name: this.translate(prefix + 'FixedQuote') },
        ];
        model.selectedDialogType = { id: messaging.isUseFixedQuote ? 1 : 0 };
        model.selectedDialogType.name = this.dialogTypeName();

        Promise.resolve(this.image.getPropertyImages({ propertyId: this.spaces.getPropertyUuid() })).then((data: any) => {
            this.zone.run(() => {
                this.model().propertyImages = data && data['asset_images'] ? data['asset_images'] : [];
            });
        });
    }

    /** v1 HomepageMessagingController.setupLanguagesData: split non-lang keys into language.data. */
    private setupLanguagesData(language: any): void {
        if (language.data) return;
        const langKeys = ['isRtl', 'name', 'code'];
        // v1 angular.copy → native deep clone (plain API JSON; no $$-keys here — no v1 ng-repeat touches it).
        const languageData = snapshotClone(language);
        langKeys.forEach((key) => delete languageData[key]);
        Object.keys(language)
            .filter((key) => langKeys.indexOf(key) === -1)
            .forEach((key) => delete language[key]);
        language.data = languageData || {};
    }

    /** homepage: the display name for the currently-selected dialog (quote) type. */
    protected dialogTypeName(): string {
        const id = this.model().selectedDialogType && this.model().selectedDialogType.id;
        const types = this.model().dialogTypes || [];
        return (types[id] && types[id].name) || (types[0] && types[0].name) || '';
    }

    /** homepage: v1 $watch(selectedDialogType.id) — sync isUseFixedQuote + name. */
    protected syncDialogType(): void {
        const id = this.model().selectedDialogType && this.model().selectedDialogType.id;
        if (id === undefined || id === null) return;
        this.model().messaging.isUseFixedQuote = id === 1;
        this.model().selectedDialogType.name = this.dialogTypeName();
    }

    /** homepage: the imageLibraryModal's on-image-selected. */
    protected updateFixedQuoteProfileImage(image: any): void {
        this.model().language[this.model().selectedLanguage.code].fixedQuoteProfileImageUrl = image ? image.url : '';
    }

    /**
     * homepage: open the native QuoteType / FixedQuoteProfileImage modal (replaces the bridged
     * `ftModal.show(id)`). Takes the v1 ft-snapshot at open (model.messaging for dialogType,
     * model.languageItems for the image modal) for the X/Cancel rollback, and lazily snapshots the
     * deferred-save panel (so its baseline is the stable post-load model, like the field editors).
     */
    protected showHomepageModal(id: string): void {
        this.panel()?.setupIfNeeded();
        if (id === 'dialogTypeModal') {
            this.homepageModalRoot = this.model().messaging;
            this.homepageModal.set('dialogTypeModal');
        } else {
            // 'homepage-messages.imageLibraryModal'
            this.homepageModalRoot = this.model().languageItems;
            this.homepageModal.set('imageLibraryModal');
        }
        this.homepageModalSnapshot = snapshotClone(this.homepageModalRoot);
    }

    /** homepage modal Done (v1 ftModal done w/o on-done): keep changes, recount the pending panel. */
    protected onHomepageModalDone(): void {
        this.closeHomepageModal();
        this.panel()?.recompute();
    }

    /** homepage modal X/Cancel (v1 ftModal cancel → snapshot.rollback): revert + recount. */
    protected onHomepageModalCancel(): void {
        if (this.homepageModalSnapshot && this.homepageModalRoot) {
            revertDiffs(this.homepageModalRoot, this.homepageModalSnapshot, computeDiffs(this.homepageModalSnapshot, this.homepageModalRoot));
        }
        this.closeHomepageModal();
        this.panel()?.recompute();
    }

    private closeHomepageModal(): void {
        this.homepageModal.set(null);
        this.homepageModalSnapshot = null;
        this.homepageModalRoot = null;
    }

    private prepare(messaging: any): void {
        messaging.isNewQuoteProcess = this.parse.bool(messaging.isNewQuoteProcess);
        messaging.installedVersionId && (messaging.installedVersionId = this.parse.intOrDefault(messaging.installedVersionId));
        messaging.poststayVersionId && (messaging.poststayVersionId = this.parse.intOrDefault(messaging.poststayVersionId));
        messaging.lastVersionId && (messaging.lastVersionId = this.parse.intOrDefault(messaging.lastVersionId));
    }

    private setupVersionManager(objectToUpgrade: any): void {
        this.version = {
            currentVersion: this.versionSelection.get(this.version && this.version.currentVersion, objectToUpgrade.poststayVersionId, objectToUpgrade.installedVersionId),
            activatedVersion: objectToUpgrade.poststayVersionId,
            updateInstalled: objectToUpgrade.upgrade !== undefined, // v1 angular.isDefined

            availableVersion: objectToUpgrade.lastVersionId,
            currentIsNewVersion: () => this.version && this.version.updateInstalled && this.version.currentVersion === this.version.availableVersion,
        };
    }

    // --- edit-modal bridges (hidden, click-only — verified by live diag) ---

    protected editConfirmationMessage(): void {
        this.openField({ editorType: 'text', fields: ['topReservationComplete'], title: this.translate('flipto.account.platform.preStay.touchpoints.ConfirmationMessage') });
    }

    protected editConfirmationLink(): void {
        this.openField({ editorType: 'html', fields: ['topHideConfirmation'], title: 'Confirmation link' });
    }

    protected editSharePrompt(): void {
        const defaultHeader = this.translate('flipto.account.platform.preStay.touchpoints.DefaultHeader');
        const homepageHeader = this.translate('flipto.account.platform.preStay.touchpoints.HomepageHeader');
        this.openField({
            editorType: 'html',
            fields: [{ name: 'header', label: defaultHeader }, { name: 'homepageHeader', label: homepageHeader }],
            tokens: ['property','city','first','last','incentive','incentive-image','incentive-short'],
            title: this.translate('flipto.account.platform.preStay.touchpoints.ShareMessage'),
        });
    }

    // --- followup edit-modal bridges (edit-followup-messages.html) ---

    protected editFollowupSubject(): void {
        this.openField({ editorType: 'text', fields: ['emailSubject'], title: this.translate('flipto.account.platform.Subject') });
    }

    protected editFollowupBody(): void {
        this.openField({ editorType: 'html', fields: ['emailBody'], tokens: ['first', 'last', 'property', 'incentive-short'], title: this.translate('flipto.account.platform.Body') });
    }

    protected editFollowupFooter(): void {
        this.openField({ editorType: 'html', fields: ['emailFooter'], tokens: ['first', 'last', 'property', 'incentive-short'], title: this.translate('flipto.account.platform.Footer') });
    }

    protected editFollowupSharePrompt(): void {
        // ⚠ v1 also passes fieldsOptions{'remove-wrapper'} — a save-time textAngular sanitize option that
        // is render-inert (gate-blind, matters only for complex rich edits; same deferred-fidelity bucket
        // as ft-html-sanitize). Omitted; simple-edit saves match byte-for-byte.
        this.openField({
            editorType: 'html',
            fields: ['header'],
            tokens: ['first','last','property','incentive-image',{text: 'incentive-short', wrapper: 'b'},'incentive'],
            title: this.translate('flipto.account.platform.preStay.touchpoints.ShareMessage'),
        });
    }

    // --- followup live preview (ported; sendFollowupEmailPreview SENDS a real email — never auto-triggered) ---

    protected sendFollowupEmailPreview(): void {
        this.livePreview
            .followupEmail({
                propertyUUID: this.spaces.getPropertyUuid(),
                touchpointUUID: this.touchpointGroupId(),
                language: this.model().selectedLanguage.code,
            })
            .then(
                () => this.notifications.show(this.translate('flipto.account.SendSampleEmailSuccess')),
                () => this.notifications.show(this.translate('flipto.account.SendSampleEmailError')),
            );
    }

    protected openFollowupLivePreview(): void {
        const language = this.getCurrentLanguage();
        language &&
            language.demos &&
            language.demos.signupCode &&
            this.livePreview.followup({ code: language.demos.signupCode, domain: this.model().messaging.domain });
    }

    // --- friend LANDING edit-modal bridges + live-previews (edit-landing-messages.html) ---

    protected editLandingSignupMessage(): void {
        this.openField({ editorType: 'html', fields: ['landingHtml'], tokens: ['first', 'last', 'profile', 'incentive-short'], title: this.translate('flipto.account.platform.preStay.touchpoints.SignUpMessage') });
    }

    protected editLandingGuestQuoteMessage(): void {
        this.openField({ editorType: 'html', fields: ['landingQuoteHtml'], tokens: ['first', 'review', 'profile', 'incentive-short'], title: this.translate('flipto.account.platform.preStay.touchpoints.SignUpWithGuestQuote') });
    }

    protected openLandingSignUpLivePreview(): void {
        const language = this.getCurrentLanguage();
        language &&
            language.demos &&
            language.demos.signupCode &&
            this.livePreview.landing({ code: language.demos.signupCode, domain: this.model().messaging.domain });
    }

    protected openLandingSignUpGustLivePreview(): void {
        const language = this.getCurrentLanguage();
        language &&
            language.demos &&
            language.demos.signupQuoteCode &&
            this.livePreview.landing({ code: language.demos.signupQuoteCode, domain: this.model().messaging.domain });
    }

    // --- friend LANDING (mobile) edit-modal bridges + live-preview (edit-mobile-messages.html) ---

    protected editMobileSignupHeader(): void {
        this.openField({ editorType: 'text', fields: ['signupHeader'], tokens: ['first', 'last', 'property'], title: this.translate('flipto.account.platform.preStay.touchpoints.GuestMessage') });
    }

    protected editMobileSignupContent(): void {
        this.openField({ editorType: 'text', fields: ['signupContent'], tokens: ['first', 'last'], title: this.translate('flipto.account.platform.preStay.touchpoints.IncentiveMessage') });
    }

    protected editMobileSignupInstructions(): void {
        this.openField({ editorType: 'text', fields: ['signupInstructions'], tokens: ['first', 'last', 'property'], title: this.translate('flipto.account.platform.preStay.touchpoints.RequestMessage') });
    }

    protected editThanksInstructions(): void {
        this.openField({ editorType: 'text', fields: ['thanksInstructions'], tokens: ['first', 'last'], title: this.translate('flipto.account.platform.preStay.touchpoints.ThanksInstructions') });
    }

    protected editThanksMessage(): void {
        this.openField({ editorType: 'text', fields: ['thanksMessage'], tokens: ['first', 'last', 'property'], title: this.translate('flipto.account.platform.preStay.touchpoints.RedirectMessage') });
    }

    protected openMobileLandingLivePreview(): void {
        const language = this.getCurrentLanguage();
        language &&
            language.demos &&
            language.demos.signupCode &&
            this.livePreview.mobileLanding({ code: language.demos.signupCode, domain: language.demos.domain });
    }

    // --- confirmation-email-image banner edit-modal bridges (edit-confirmation-email-image-messages.html) ---

    // Shared by confirmation-email-image + poststay-email-image (same fields); the cache key
    // is keyed off touchpointType so the two touchpoints don't share a bridged modal instance.
    protected editBannerHeading(): void {
        this.openField({ editorType: 'text', fields: ['header'], required: true, title: this.translate('flipto.account.platform.BannerHeading') });
    }

    protected editBannerText(): void {
        this.openField({ editorType: 'html', fields: ['text'], required: true, title: this.translate('flipto.account.platform.BannerText') });
    }

    protected editBannerButtonText(): void {
        this.openField({ editorType: 'text', fields: ['buttonText'], required: true, title: this.translate('flipto.account.platform.BannerButtonText') });
    }

    // --- post-stay invitation-email edit-modal bridges (edit-invitation-email-messages.html) ---

    protected editInvitationSubject(): void {
        this.openField({ editorType: 'text', fields: ['emailSubject'], title: this.translate('flipto.account.platform.Subject') });
    }

    protected editInvitationBody(): void {
        this.openField({ editorType: 'html', fields: ['emailBody'], tokens: ['first', 'last', 'property', 'participation-incentive-short', 'share-incentive-short', 'photo-contest-name', 'photo-contest-winner-incentive-short'], title: this.translate('flipto.account.platform.Body') });
    }

    protected editInvitationFooter(): void {
        this.openField({ editorType: 'html', fields: ['emailFooter'], tokens: ['first', 'last', 'property', 'participation-incentive-short', 'share-incentive-short', 'photo-contest-name', 'photo-contest-winner-incentive-short'], title: this.translate('flipto.account.platform.Footer') });
    }

    protected sendPoststayEmailPreview(): void {
        this.livePreview
            .poststayEmail({
                propertyUUID: this.spaces.getPropertyUuid(),
                touchpointUUID: this.touchpointGroupId(),
                language: this.model().selectedLanguage.code,
            })
            .then(
                () => this.notifications.show(this.translate('flipto.account.SendSampleEmailSuccess')),
                () => this.notifications.show(this.translate('flipto.account.SendSampleEmailError')),
            );
    }

    // --- post-stay reminder-emails edit-modal bridges (edit-reminder-emails-messages.html) ---
    // Body/Footer share the same available-tags + token list (verbatim from the v1 template).

    private static readonly POSTSTAY_EMAIL_TOKENS: FieldTokens =
        ['first', 'last', 'property', 'participation-incentive-short', 'share-incentive-short', 'photo-contest-name', 'photo-contest-winner-incentive-short'];

    protected editReminderNonViewSubject(): void {
        this.openField({ editorType: 'text', fields: ['nonViewEmailSubject'], title: this.translate('flipto.account.platform.Subject') });
    }

    protected editReminderNonViewBody(): void {
        this.openField({ editorType: 'html', fields: ['nonViewEmailBody'], tokens: FtTouchpointMessagingComponent.POSTSTAY_EMAIL_TOKENS, title: this.translate('flipto.account.platform.Body') });
    }

    protected editReminderNonViewFooter(): void {
        this.openField({ editorType: 'html', fields: ['nonViewEmailFooter'], tokens: FtTouchpointMessagingComponent.POSTSTAY_EMAIL_TOKENS, title: this.translate('flipto.account.platform.Footer') });
    }

    protected editReminderAfterViewSubject(): void {
        this.openField({ editorType: 'text', fields: ['afterViewEmailSubject'], title: this.translate('flipto.account.platform.Subject') });
    }

    protected editReminderAfterViewBody(): void {
        this.openField({ editorType: 'html', fields: ['afterViewEmailBody'], tokens: FtTouchpointMessagingComponent.POSTSTAY_EMAIL_TOKENS, title: this.translate('flipto.account.platform.Body') });
    }

    protected editReminderAfterViewFooter(): void {
        this.openField({ editorType: 'html', fields: ['afterViewEmailFooter'], tokens: FtTouchpointMessagingComponent.POSTSTAY_EMAIL_TOKENS, title: this.translate('flipto.account.platform.Footer') });
    }

    // reminder-emails launch previews (port; SEND a real email — never auto-triggered).
    protected sendPoststayNonViewEmailPreview(): void {
        this.livePreview
            .poststayNonViewEmail({
                propertyUUID: this.spaces.getPropertyUuid(),
                touchpointUUID: this.touchpointGroupId(),
                language: this.model().selectedLanguage.code,
            })
            .then(
                () => this.notifications.show(this.translate('flipto.account.SendSampleEmailSuccess')),
                () => this.notifications.show(this.translate('flipto.account.SendSampleEmailError')),
            );
    }

    protected sendPoststayAfterViewEmailPreview(): void {
        this.livePreview
            .poststayAfterViewEmail({
                propertyUUID: this.spaces.getPropertyUuid(),
                touchpointUUID: this.touchpointGroupId(),
                language: this.model().selectedLanguage.code,
            })
            .then(
                () => this.notifications.show(this.translate('flipto.account.SendSampleEmailSuccess')),
                () => this.notifications.show(this.translate('flipto.account.SendSampleEmailError')),
            );
    }

    // --- post-stay reengagement-emails edit-modal bridges (edit-reengagement-emails-messages.html) ---
    //     Same available-tags + token list as reminder; screens no-quote / no-photo.

    /** The rule-warning gate: v1 `ng-if="!(model.messaging.isPhotoExplorerActive | parse:'bool')"`. */
    protected get reengagementPhotoExplorerActive(): boolean {
        return this.parse.bool(this.model()?.messaging?.isPhotoExplorerActive);
    }

    protected editReengagementNoQuoteSubject(): void {
        this.openField({ editorType: 'text', fields: ['noQuoteEmailSubject'], title: this.translate('flipto.account.platform.Subject') });
    }

    protected editReengagementNoQuoteBody(): void {
        this.openField({ editorType: 'html', fields: ['noQuoteEmailBody'], tokens: FtTouchpointMessagingComponent.POSTSTAY_EMAIL_TOKENS, title: this.translate('flipto.account.platform.Body') });
    }

    protected editReengagementNoQuoteFooter(): void {
        this.openField({ editorType: 'html', fields: ['noQuoteEmailFooter'], tokens: FtTouchpointMessagingComponent.POSTSTAY_EMAIL_TOKENS, title: this.translate('flipto.account.platform.Footer') });
    }

    protected editReengagementNoPhotoSubject(): void {
        this.openField({ editorType: 'text', fields: ['noPhotoEmailSubject'], title: this.translate('flipto.account.platform.Subject') });
    }

    protected editReengagementNoPhotoBody(): void {
        this.openField({ editorType: 'html', fields: ['noPhotoEmailBody'], tokens: FtTouchpointMessagingComponent.POSTSTAY_EMAIL_TOKENS, title: this.translate('flipto.account.platform.Body') });
    }

    protected editReengagementNoPhotoFooter(): void {
        this.openField({ editorType: 'html', fields: ['noPhotoEmailFooter'], tokens: FtTouchpointMessagingComponent.POSTSTAY_EMAIL_TOKENS, title: this.translate('flipto.account.platform.Footer') });
    }

    // reengagement launch previews (port; SEND a real email — never auto-triggered).
    protected sendPoststayNoQuoteEmailPreview(): void {
        this.livePreview
            .poststayNoQuoteEmail({
                propertyUUID: this.spaces.getPropertyUuid(),
                touchpointUUID: this.touchpointGroupId(),
                language: this.model().selectedLanguage.code,
            })
            .then(
                () => this.notifications.show(this.translate('flipto.account.SendSampleEmailSuccess')),
                () => this.notifications.show(this.translate('flipto.account.SendSampleEmailError')),
            );
    }

    protected sendPoststayNoPhotoEmailPreview(): void {
        this.livePreview
            .poststayNoPhotoEmail({
                propertyUUID: this.spaces.getPropertyUuid(),
                touchpointUUID: this.touchpointGroupId(),
                language: this.model().selectedLanguage.code,
            })
            .then(
                () => this.notifications.show(this.translate('flipto.account.SendSampleEmailSuccess')),
                () => this.notifications.show(this.translate('flipto.account.SendSampleEmailError')),
            );
    }

    // --- photo-advocacy contest emails (finalist/reminder/activity/winner) edit-modal bridges ---
    //     Single-screen email editors sharing emailSubject/emailBody/emailFooter (keyed by touchpointType).

    private static readonly PHOTO_EMAIL_TOKENS: FieldTokens = ['first', 'last', 'email', 'property', 'incentive', 'incentive-short', 'contest-name'];

    protected editPhotoEmailSubject(): void {
        this.openField({ editorType: 'text', fields: ['emailSubject'], tokens: ['contest-name'], title: this.translate('flipto.account.platform.Subject') });
    }

    protected editPhotoEmailBody(): void {
        this.openField({ editorType: 'html', fields: ['emailBody'], tokens: FtTouchpointMessagingComponent.PHOTO_EMAIL_TOKENS, title: this.translate('flipto.account.platform.Body') });
    }

    protected editPhotoEmailFooter(): void {
        this.openField({ editorType: 'html', fields: ['emailFooter'], tokens: FtTouchpointMessagingComponent.PHOTO_EMAIL_TOKENS, title: this.translate('flipto.account.platform.Footer') });
    }

    /** v1 messaging.controller.js:103 — gates the launch-preview link. */
    protected canSendSampleEmail(): boolean {
        const lang = this.getCurrentLanguage();
        return !!lang && this.parse.bool(lang.isCanSendSampleEmail);
    }

    // photo-advocacy launch previews (port; SEND a real email — never auto-triggered).
    private sendPhotoEmailPreview(method: string): void {
        this.livePreview[method]({
            propertyUUID: this.spaces.getPropertyUuid(),
            touchpointUUID: this.touchpointGroupId(),
            language: this.model().selectedLanguage.code,
        }).then(
            () => this.notifications.show(this.translate('flipto.account.SendSampleEmailSuccess')),
            () => this.notifications.show(this.translate('flipto.account.SendSampleEmailError')),
        );
    }

    protected sendFinalistEmailPreview(): void {
        this.sendPhotoEmailPreview('finalistEmail');
    }

    protected sendReminderEmailPreview(): void {
        this.sendPhotoEmailPreview('reminderEmail');
    }

    protected sendActivityEmailPreview(): void {
        this.sendPhotoEmailPreview('activityEmail');
    }

    protected sendWinnerEmailPreview(): void {
        this.sendPhotoEmailPreview('winnerEmail');
    }

    // --- HOMEPAGE-EMAIL "Redeem Email" edit-modal bridges + preview (edit-homepageEmail-messages.html) ---

    protected editHomepageEmailSubject(): void {
        this.openField({ editorType: 'text', fields: ['emailSubject'], title: this.translate('flipto.account.platform.Subject') });
    }

    protected editHomepageEmailBody(): void {
        this.openField({ editorType: 'html', fields: ['emailBody'], tokens: ['first', 'last', 'property'], title: this.translate('flipto.account.platform.Body') });
    }

    protected editHomepageEmailFooter(): void {
        this.openField({ editorType: 'html', fields: ['emailFooter'], tokens: ['first', 'last', 'property'], title: this.translate('flipto.account.platform.Footer') });
    }

    protected sendRedeemEmailPreview(): void {
        this.livePreview
            .redeemEmail({
                propertyUUID: this.spaces.getPropertyUuid(),
                touchpointUUID: this.touchpointGroupId(),
                language: this.model().selectedLanguage.code,
            })
            .then(
                () => this.notifications.show(this.translate('flipto.account.SendSampleEmailSuccess')),
                () => this.notifications.show(this.translate('flipto.account.SendSampleEmailError')),
            );
    }

    // --- PHOTO-RIVER "Story River" edit-modal bridges + live-preview (edit-photo-river-messages.html) ---

    protected editPhotoRiverHeader(): void {
        this.openField({ editorType: 'text', fields: ['header'], required: true, title: this.translate('flipto.account.Heading') });
    }

    protected editPhotoRiverSubtitle(): void {
        this.openField({ editorType: 'html', fields: ['subtitle'], required: true, title: this.translate('flipto.account.Description') });
    }

    protected editPhotoRiverRampBody(): void {
        this.openField({ editorType: 'text', fields: ['ramp_body'], required: true, title: this.translate('flipto.account.GalleryLinkMessage') });
    }

    protected openPhotoRiverLivePreview(): void {
        const language = this.getCurrentLanguage();
        language && language.code && language.fullCode && this.livePreview.photoRiver({ code: language.code, fullCode: language.fullCode });
    }

    // --- HOMEPAGE "Homepage Component" field editors (edit-homepage-messages.html), now NATIVE.
    //     openField edits model.languageItems[currentLang].data[field] (FtHtml/TextField resolve the
    //     .data nesting); the panel ([live]=model.messaging for homepage) diffs them → HomepageMixedData.
    //     The v1 'replace-wrapper' fieldsOption (UnlockMessage) is a textAngular save-sanitize option —
    //     render-inert + simple-edit saves match; omitted (the deferred-fidelity bucket). ---

    protected editHomepageGuestQuote(): void {
        this.openField({ editorType: 'html', fields: ['parentQuoteHtml'], tokens: ['review', 'date'], title: this.translate('flipto.account.platform.homepage.touchpoints.GuestQuote') });
    }

    protected editHomepageUnlockMessage(): void {
        this.openField({ editorType: 'html', fields: ['parentMessageHtml'], tokens: ['profile', 'first', 'last'], title: this.translate('flipto.account.platform.homepage.touchpoints.UnlockMessage') });
    }

    protected editHomepageFixedQuote(): void {
        this.openField({ editorType: 'text', fields: ['fixedQuoteText'], title: this.translate('flipto.account.platform.homepage.touchpoints.FixedQuote') });
    }

    protected editHomepageFixedQuoteName(): void {
        const first = this.translate('flipto.account.platform.homepage.touchpoints.FirstName');
        const last = this.translate('flipto.account.platform.homepage.touchpoints.LastName');
        this.openField({ editorType: 'text', fields: [{ name: 'fixedQuoteFirst', label: first }, { name: 'fixedQuoteLast', label: last }], title: this.translate('flipto.account.platform.homepage.touchpoints.FixedQuoteName') });
    }

    protected editHomepageBookNow(): void {
        this.openField({ editorType: 'html', fields: ['bookNowHtml'], title: this.translate('flipto.account.platform.homepage.touchpoints.BookNowMessage') });
    }

    protected editHomepageSignup(): void {
        this.openField({ editorType: 'html', fields: ['signupHtml'], title: this.translate('flipto.account.platform.homepage.touchpoints.SignupMessage') });
    }

    // --- live preview (port; never auto-triggered) ---

    protected openConfirmationLivePreview(): void {
        const language = this.getCurrentLanguage();
        if (!language) return;
        const splitEmail = this.identity.user.email.split('@');
        splitEmail[0] += '+' + Date.now();
        const modifiedEmail = splitEmail.join('@');
        language.code &&
            language.fullCode &&
            this.livePreview.confirmation({
                code: language.code,
                fullCode: language.fullCode,
                email: modifiedEmail,
                first: this.identity.user.firstName,
                last: this.identity.user.lastName,
            });
    }

    // --- native pending-changes panel + field editors (ftModal phase 2 / WS3) ---

    /**
     * The live model root the native pending-changes panel tracks. Homepage snapshots
     * `model.messaging` (the v1 HomepageMessagingController's `ft-snapshot="model.messaging"`): its
     * diffs are `['languages','item',i,'data',field]` (→ HomepageMixedData language branch,
     * `path[0]==='languages'`) + `['isUseFixedQuote']` (the dialogType change). Every other branch
     * snapshots `model.languageItems` (diffs `[i,(data,)field]` → MultiLanguageData).
     */
    protected get panelLiveRoot(): any {
        return this.touchpointType() === 'homepage' ? this.model()?.messaging : this.model()?.languageItems;
    }

    /**
     * Open a field editor on `model.languageItems[*][field]` through the native ft-field-editor-modal.
     * Replicates the v1 FieldEditor factory coercion (fieldEditors.js): a `text` editor WITH tokens
     * becomes an `html` editor in mode=text (textAngular + Insert-Token); plain `html` is WYSIWYG.
     */
    private openField(opts: { editorType: 'text' | 'html'; fields: any; title: string; tokens?: FieldTokens; readonlytext?: string; required?: boolean; size?: string }): void {
        this.panel()?.setupIfNeeded();
        const ds = this.model().languageItems;
        this.fieldDatasource = ds;
        this.fieldSnapshot = snapshotClone(ds);
        // Tokens are real arrays (strings or {text,wrapper} objects — the v1 expression-string forms,
        // parsed at port time). FtHtmlField renders the Insert-Token dropdown items from them directly.
        // ⚠ DEFERRED (gate-blind): the v1 'available-tags'/'remove-wrapper'/'replace-wrapper' fieldsOptions
        // are textAngular save-sanitize options — render-inert (collapsed toolbar unchanged) + simple-edit
        // saves match; omitted (same bucket as ft-html-sanitize). `required` is passed (cosmetic; the native
        // Done isn't validity-gated, so it only mirrors the input attr).
        const hasTokens = !!opts.tokens?.length;
        let type: 'text' | 'html' = opts.editorType;
        const fieldsOptions: any = {};
        if (opts.editorType === 'text' && hasTokens) {
            // v1 FieldEditor factory: text + tokens → html, mode=text (textAngular + Insert-Token).
            type = 'html';
            fieldsOptions.mode = 'text';
            fieldsOptions.tokens = opts.tokens;
        } else if (opts.editorType === 'html') {
            if (hasTokens) fieldsOptions.tokens = opts.tokens;
            if (opts.readonlytext) fieldsOptions.readonlytext = opts.readonlytext;
        }
        if (opts.required) fieldsOptions.required = true;
        this.fieldEditorConfig.set({
            type,
            datasource: ds,
            fields: opts.fields,
            fieldsOptions,
            title: opts.title,
            size: opts.size,
            onCancel: () => this.rollbackField(),
            onClose: () => this.afterFieldClose(),
        });
    }

    /** Field cancel: revert the field's own edit (the panel's deferred snapshot is separate). */
    private rollbackField(): void {
        if (!this.fieldSnapshot || !this.fieldDatasource) return;
        revertDiffs(this.fieldDatasource, this.fieldSnapshot, computeDiffs(this.fieldSnapshot, this.fieldDatasource));
    }

    private afterFieldClose(): void {
        this.fieldSnapshot = null;
        this.fieldDatasource = null;
        this.panel()?.recompute();
    }

    /**
     * Panel Save → the v1 commit (MultiLanguageData over model.languageItems), then re-snapshot. The
     * native diffs are rooted at model.languageItems (paths [i,(data,)field]); commit splits main-vs-
     * upgrade by 'model.messaging.upgrade.languages.item' (never present on the converted branches) and
     * getFixedLanguagePath crops the languageItems prefix (a no-op on the already-rooted native paths);
     * MultiLanguageData reads path[0] (lang index) + _last(path) (field) — both satisfied. flattenChange
     * is the defensive cert-email guard (a no-op on these kind:'E' diffs).
     */
    protected onPanelSave(diffs: any[]): void {
        this.commit((diffs || []).map((c: any) => flattenChange(c)));
        this.panel()?.commit();
    }

    /** Panel Cancel: the panel already reverted model.languageItems; refresh the count. */
    protected onPanelCancel(): void {
        this.panel()?.recompute();
    }

    // --- save (the FULL union the v1 controller PUTs — bridged commit) ---

    commit(deepDiffChanges: any[]): void {
        // homepage (the SPECIAL HomepageMessagingController) — a single HomepageMixedData PUT, no versions.
        if (this.touchpointType() === 'homepage') {
            const fixedLanguagePath = map(deepDiffChanges, (change: any) => {
                const copy = snapshotClone(change); // v1 angular.copy of a plain deep-diff change object
                if (change.path.join('.').indexOf('model.messaging.languages.item') !== -1) {
                    copy.path.splice(0, 2);
                }
                return copy;
            });
            const changes = this.changesTransformer(this.transformType.HomepageMixedData, fixedLanguagePath, this.messaging());
            const url = this.endpointUrl.getDataUrl(this.layerName(), this.touchpointType(), this.touchpointGroupId(), this.spaces.getPropertyUuid());
            this.messagingApi.put(url, changes);
            return;
        }
        const mainChanges = filter(deepDiffChanges, (change: any) => change.path.join('.').indexOf('model.messaging.upgrade.languages.item') === -1);
        const upgradeChanges = filter(deepDiffChanges, (change: any) => change.path.join('.').indexOf('model.messaging.upgrade.languages.item') !== -1);
        this.saveData(mainChanges, this.version.activatedVersion);
        this.messaging().upgrade && this.saveData(upgradeChanges, this.messaging().upgrade.poststayVersionId);
    }

    private saveData(deepDiffChanges: any[], version: any): void {
        if (isEmpty(deepDiffChanges)) return;
        const fixedLanguagePath = this.getFixedLanguagePath(deepDiffChanges);
        const changes = this.changesTransformer(this.transformType.MultiLanguageData, fixedLanguagePath, this.messaging().languages.item);
        const url = this.endpointUrl.getDataUrl(this.layerName(), this.touchpointType(), this.touchpointGroupId(), this.spaces.getPropertyUuid(), version);
        this.messagingApi.put(url, changes);
    }

    private getFixedLanguagePath(deepDiffChanges: any[]): any[] {
        return map(deepDiffChanges, (change: any) => {
            const copy = snapshotClone(change); // v1 angular.copy of a plain deep-diff change object

            const path = change.path.join('.');
            const patchesToCrop = ['model.languageItems', 'model.messaging.languages.item', 'model.messaging.upgrade.languages.item'];
            patchesToCrop.forEach((toCrop: string) => {
                if (path.indexOf(toCrop) !== -1) {
                    copy.path.splice(0, toCrop.split('.').length);
                }
            });
            return copy;
        });
    }
}

/**
 * Flatten a deep-diff `kind:'A'` (array) change into a flat index path for the v1 MultiLanguageData
 * transformer. For the in-place language-field edits here deep-diff emits `kind:'E'` (path
 * `[i,(data,)field]`) so this is a no-op — it's the defensive cert-email guard, kept identical in
 * case deep-diff ever emits a whole-array `kind:'A'` diff.
 */
function flattenChange(c: any, base: any[] = []): any {
    if (c && c.kind === 'A') {
        return flattenChange(c.item, base.concat(c.path || []).concat([c.index]));
    }
    return Object.assign({}, c, { path: base.concat((c && c.path) || []) });
}
